<template>
  <div class="number-range-filter pa-3" data-testid="number-range-filter">
    <div class="number-range-filter__summary mb-3" data-testid="number-range-filter__summary">
      <span class="caption grey--text">{{ summaryText }}</span>
    </div>
    <div class="d-flex align-center number-range-filter__inputs" data-testid="number-range-filter__inputs">
      <v-text-field
        v-model.number="localMin"
        type="number"
        label="Min Value"
        dense
        outlined
        hide-details
        clearable
        :min="0"
        class="number-range-filter__input"
        data-testid="number-range-filter__min-input"
        @keydown="blockInvalidNumberKeys"
        @click:clear="localMin = null"
      />
      <span class="mx-2 grey--text number-range-filter__separator" data-testid="number-range-filter__separator">—</span>
      <v-text-field
        v-model.number="localMax"
        type="number"
        label="Max Value"
        dense
        outlined
        hide-details
        clearable
        :min="0"
        class="number-range-filter__input"
        data-testid="number-range-filter__max-input"
        @keydown="blockInvalidNumberKeys"
        @click:clear="localMax = null"
      />
    </div>
    <v-divider class="mt-3" data-testid="number-range-filter__divider" />
    <div class="d-flex justify-end mt-2" data-testid="number-range-filter__footer">
      <v-btn
        text
        small
        @click="onClear"
        data-testid="number-range-filter__clear-btn"
        class="mr-1"
      >
        <span>Clear</span>
      </v-btn>
      <v-btn
        text
        small
        @click="onCancel"
        data-testid="number-range-filter__cancel-btn"
        class="mr-1"
      >
        <span>Cancel</span>
      </v-btn>
      <v-btn
        color="primary"
        small
        depressed
        @click="onApply"
        :disabled="!hasValidInput"
        data-testid="number-range-filter__apply-btn"
      >
        <span>Apply</span>
      </v-btn>
    </div>
  </div>
</template>

<script>
import './NumberRangeFilter.scss';

export default {
  name: 'NumberRangeFilter',
  props: {
    value: {
      type: Object,
      default: () => ({ min: null, max: null }),
    },
    /**
     * Data-driven range: { min, max } computed from the column's actual values.
     * Used to pre-fill inputs when no active filter is applied.
     */
    dataRange: {
      type: Object,
      default: () => ({ min: null, max: null }),
    },
  },
  data() {
    return {
      localMin: null,
      localMax: null,
    };
  },
  computed: {
    hasValidInput() {
      const hasMin = this.localMin !== null && this.localMin !== '' && !isNaN(this.localMin) && Number(this.localMin) >= 0;
      const hasMax = this.localMax !== null && this.localMax !== '' && !isNaN(this.localMax) && Number(this.localMax) >= 0;
      if (!hasMin && !hasMax) return false;
       if (hasMin && hasMax) return Number(this.localMin) <= Number(this.localMax);
       return true;
     },
    summaryText() {
      const hasMin = this.localMin !== null && this.localMin !== '' && !isNaN(this.localMin) && Number(this.localMin) >= 0;
      const hasMax = this.localMax !== null && this.localMax !== '' && !isNaN(this.localMax) && Number(this.localMax) >= 0;
      if (hasMin && hasMax) return `${this.localMin} — ${this.localMax}`;
      if (hasMin) return `≥ ${this.localMin}`;
      if (hasMax) return `≤ ${this.localMax}`;
      return 'Enter a number range';
    },
  },
  watch: {
    value: {
      handler(val) {
        this.initFromValue(val);
      },
      immediate: true,
      deep: true,
    },
    dataRange: {
      handler() {
        // Re-seed defaults only when there is no active filter applied
        const hasActiveFilter =
          (this.value && this.value.min !== null && this.value.min !== undefined && this.value.min !== '') ||
          (this.value && this.value.max !== null && this.value.max !== undefined && this.value.max !== '');
        if (!hasActiveFilter) {
          this.initFromValue(null);
        }
      },
      deep: true,
    },
  },
  methods: {
    initFromValue(val) {
        const hasActiveFilter =
         val &&
         typeof val === 'object' &&
         !Array.isArray(val) &&
         ((val.min !== null && val.min !== undefined && val.min !== '') ||
           (val.max !== null && val.max !== undefined && val.max !== ''));        
      if (hasActiveFilter) {
        // Restore the active filter values
        this.localMin = val.min !== undefined && val.min !== null ? val.min : null;
        this.localMax = val.max !== undefined && val.max !== null ? val.max : null;
      } else {
        // No active filter — seed from column data range for convenience
        const dr = this.dataRange || {};
        this.localMin = (dr.min !== null && dr.min !== undefined) ? dr.min : null;
        this.localMax = (dr.max !== null && dr.max !== undefined) ? dr.max : null;
      }
    },
    onApply() {
      if (!this.hasValidInput) return;
      const hasMin = this.localMin !== null && this.localMin !== '' && !isNaN(this.localMin) && Number(this.localMin) >= 0;
      const hasMax = this.localMax !== null && this.localMax !== '' && !isNaN(this.localMax) && Number(this.localMax) >= 0;
      this.$emit('apply', {
        min: hasMin ? Number(this.localMin) : null,
        max: hasMax ? Number(this.localMax) : null,
      });
    },
    onClear() {
      this.localMin = null;
      this.localMax = null;
      this.$emit('apply', null);
    },
    onCancel() {
      this.$emit('close');
    },
    blockInvalidNumberKeys(event) {
      // Prevent e, E, +, - which are valid in HTML number inputs but not wanted here
      if (['e', 'E', '+', '-'].includes(event.key)) {
        event.preventDefault();
      }
    },
  },
};
</script>
